import {
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
  getIdToken,
  type User,
} from "firebase/auth";
import { LRUCache } from "lru-cache";
import type { AuthProvider } from "ra-core";
import { authObj } from "@/firebase-config";
import { goToLogin } from "@/utils";
import httpClient from "@/dataprovider/http-client";
import { getApiUrl } from "@/constants";
import {
  clearStoredTenantBranding,
  fetchAndStorePublicTenantBranding,
  fetchAndStoreTenantBrandingTheme,
} from "@/utils/tenant-branding-cache";
import {
  hasPermission,
  parsePermissionsArray,
  shouldRedirectToDistributorPortal,
} from "@/utils/permission-utils";
import { postLoginRedirectPath, readStoredTenantRetirement } from "@/utils/tenant-retirement";
import type { PermissionRecord } from "@/types/permissions";

const cache = new LRUCache<string, PermissionRecord>({
  max: 50,
  ttl: 1000 * 60 * 5,
});

/** Clear cached react-admin permissions (e.g. after switching Admin ↔ Distributor principal). */
export const clearPermissionsCache = (uid?: string): void => {
  if (uid) {
    cache.delete(uid);
    return;
  }
  cache.clear();
};

/** Seed permission cache after distributor establish-session (avoids stale admin cache). */
export const seedPermissionsCache = (uid: string, permissions: PermissionRecord): void => {
  cache.set(uid, permissions);
};

let authStatePromise: Promise<User | null> | null = null;

const waitForAuthState = (): Promise<User | null> => {
  if (authObj.currentUser) {
    return Promise.resolve(authObj.currentUser);
  }

  if (authStatePromise) {
    return authStatePromise;
  }

  authStatePromise = new Promise((resolve) => {
    const timeout = setTimeout(() => {
      authStatePromise = null;
      resolve(null);
    }, 5000);

    const unsubscribe = onAuthStateChanged(authObj, (authUser) => {
      clearTimeout(timeout);
      unsubscribe();
      authStatePromise = null;
      resolve(authUser ?? null);
    });
  });

  return authStatePromise;
};

const UNAUTHORIZED_LOGIN_MESSAGE =
  "You don't have access to this app. Ask an administrator if you need help.";
const SUPER_ADMIN_IMPERSONATE_PERMISSION = "superAdmin.impersonate";

const clearSession = async (reason?: "unauthorized"): Promise<void> => {
  await signOut(authObj);
  localStorage.clear();
  sessionStorage.clear();
  cache.clear();
  const { href } = window.location;
  const url = new URL(href);
  const params = new URLSearchParams({ reload: String(Date.now()) });
  if (reason) {
    params.set("reason", reason);
  }
  window.location.replace(`${url.origin}/#/login?${params.toString()}`);
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function httpLikeError(error: unknown): { status?: number; json?: unknown } | null {
  if (!isRecord(error)) return null;
  return error;
}

function errorMessageFromHttpLike(error: unknown): string | undefined {
  const err = httpLikeError(error);
  const json = err?.json;
  if (!isRecord(json)) return undefined;
  const msg = json.message;
  return typeof msg === "string" ? msg : undefined;
}

function isUnauthorizedAccessError(error: unknown): boolean {
  const message = errorMessageFromHttpLike(error)?.toLowerCase() ?? "";
  // These are auth/session problems, not "no app access" problems.
  if (message.includes("invalid or expired token")) {
    return false;
  }
  return true;
}

async function fetchCurrentAdminIdentity(): Promise<{
  id: string;
  fullName: string;
  email?: string;
}> {
  const url = `${getApiUrl()}/admins/me`;
  const response = await httpClient(url);
  const payload = response.json;
  const row =
    isRecord(payload) && Array.isArray(payload.rows) && payload.rows.length > 0
      ? payload.rows[0]
      : null;
  if (!isRecord(row) || typeof row.id !== "string") {
    throw new Error("Unable to resolve current user identity.");
  }
  const fullName =
    typeof row.name === "string" && row.name.trim().length > 0
      ? row.name.trim()
      : typeof row.email === "string" && row.email.trim().length > 0
        ? row.email.trim()
        : "Signed-in user";
  const email =
    typeof row.email === "string" && row.email.trim().length > 0 ? row.email.trim() : undefined;
  return { id: row.id, fullName, email };
}

const fetchPermissionsFromAPI = async (): Promise<string[]> => {
  try {
    const url = `${getApiUrl()}/admins/me/permissions`;
    const response = await httpClient(url);

    const { json } = response;
    if (!json) {
      return [];
    }

    const permissionsRaw = Array.isArray(json)
      ? json
      : isRecord(json) && Array.isArray(json.rows)
        ? json.rows
        : [];

    if (!Array.isArray(permissionsRaw)) {
      console.error("Invalid permissions format from API:", json);
      return [];
    }

    return permissionsRaw.filter((p): p is string => typeof p === "string");
  } catch (error: unknown) {
    const err = httpLikeError(error);
    const status = err?.status;
    const jsonBody = err?.json;
    const statusCode = isRecord(jsonBody) ? jsonBody.statusCode : undefined;
    if (status === 401 || statusCode === 401) {
      throw error;
    }
    throw error;
  }
};

const getCachedPermissions = async (force = false): Promise<PermissionRecord> => {
  const user = await waitForAuthState();

  if (!user) {
    return {};
  }

  const cacheKey = user.uid;

  let shouldForceRefresh = force;
  if (!shouldForceRefresh && typeof window !== "undefined") {
    const bustTimestamp = sessionStorage.getItem("permissionsCacheBust");
    if (bustTimestamp) {
      const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
      if (parseInt(bustTimestamp, 10) > fiveMinutesAgo) {
        shouldForceRefresh = true;
        cache.delete(cacheKey);
        // Clear the bust flag so subsequent calls within the same page-load
        // session are served from the in-memory cache (avoids repeated API
        // calls on every route change after a tenant switch).
        sessionStorage.removeItem("permissionsCacheBust");
      }
    }
  }

  if (!shouldForceRefresh) {
    const cached = cache.get(cacheKey);
    if (cached) {
      return cached;
    }
  }

  const permissionArray = await fetchPermissionsFromAPI();

  const permissions =
    permissionArray && permissionArray.length > 0 ? parsePermissionsArray(permissionArray) : {};

  cache.set(cacheKey, permissions);
  return permissions;
};

export type FinalizeFirebaseLoginOptions = {
  /**
   * Deprecated on frontend: unknown/uninvited Firebase users are cleaned up by backend auth guard.
   * Kept only for call-site compatibility.
   */
  revokeFirebaseUserIfUnauthorized?: boolean;
};

/**
 * Completes sign-in by loading permissions. Unknown/uninvited Firebase users are cleaned up by
 * backend auth guard, not by frontend.
 */
export const finalizeFirebaseLogin = async (
  user: User,
  options?: FinalizeFirebaseLoginOptions
): Promise<{ redirectTo: string }> => {
  void options;

  let idToken = await getIdToken(user, true);
  localStorage.setItem("AUTH_TOKEN", idToken);
  localStorage.setItem("EXPIRE_TIME", String(Date.now() + 3000 * 1000));

  // Pin principal=admin before reading permissions (dual-role phones may still be distributor).
  let establishedAdmin = false;
  try {
    await httpClient(`${getApiUrl()}/admins/establish-session`, {
      method: "POST",
      body: JSON.stringify({}),
    });
    idToken = await getIdToken(user, true);
    localStorage.setItem("AUTH_TOKEN", idToken);
    establishedAdmin = true;
  } catch (error: unknown) {
    const message = (errorMessageFromHttpLike(error) ?? "").toLowerCase();
    const allowContinueWithoutAdminPrincipal =
      message.includes("tenantid is required") || message.includes("complete staff invite");
    if (!allowContinueWithoutAdminPrincipal) {
      // Tenanted dual-role / network failures must not leave principal=distributor on staff login.
      const err = httpLikeError(error);
      if (err?.status === 401 || err?.status === 403) {
        await clearSession("unauthorized");
        throw new Error(
          errorMessageFromHttpLike(error) ??
            "Staff login failed. If you are a distributor, use the distributor portal sign-in page."
        );
      }
      throw error instanceof Error
        ? error
        : new Error(errorMessageFromHttpLike(error) ?? "Staff login failed");
    }
  }

  let permissionArray: string[];
  try {
    permissionArray = await fetchPermissionsFromAPI();
  } catch (error: unknown) {
    const err = httpLikeError(error);
    const jsonBody = err?.json;
    const statusCode = isRecord(jsonBody) ? jsonBody.statusCode : undefined;
    if (err?.status === 401 || statusCode === 401) {
      if (isUnauthorizedAccessError(error)) {
        await clearSession("unauthorized");
        throw new Error(UNAUTHORIZED_LOGIN_MESSAGE);
      }
      await clearSession();
      throw new Error(
        errorMessageFromHttpLike(error) ?? "Invalid or expired token. Please sign in again."
      );
    }
    throw error;
  }

  if (!permissionArray || permissionArray.length === 0) {
    await clearSession("unauthorized");
    throw new Error(UNAUTHORIZED_LOGIN_MESSAGE);
  }

  const isSuperAdmin = permissionArray.includes(SUPER_ADMIN_IMPERSONATE_PERMISSION);
  if (isSuperAdmin) {
    localStorage.setItem("isSuperAdmin", "1");
    const impersonatedTenantId = localStorage.getItem("impersonatedTenantId");
    if (!impersonatedTenantId) {
      clearStoredTenantBranding();
      await fetchAndStorePublicTenantBranding().catch(() => {
        /* hostname branding optional */
      });
      return { redirectTo: "/select-tenant" };
    }
  } else {
    localStorage.removeItem("isSuperAdmin");
  }

  const permissions = parsePermissionsArray(permissionArray);
  if (!establishedAdmin && shouldRedirectToDistributorPortal(permissions)) {
    await clearSession("unauthorized");
    throw new Error(
      "Use the distributor portal sign-in page (/fmb-thali-distributor/login) for distributor access."
    );
  }

  cache.set(user.uid, permissions);
  localStorage.removeItem("distributorPortalId");

  await fetchAndStoreTenantBrandingTheme().catch(() => {
    /* optional; theme falls back to code defaults */
  });

  // Staff login never redirects to distributor portal (use /fmb-thali-distributor/login).
  // A tenant that moved to Jamaat360 lands on the notice screen instead of the dashboard.
  return { redirectTo: postLoginRedirectPath(readStoredTenantRetirement()) };
};

const authProvider: AuthProvider = {
  login: async ({ username, password }) => {
    try {
      const { user } = await signInWithEmailAndPassword(authObj, username, password);
      return await finalizeFirebaseLogin(user);
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw error;
      }
      const message =
        isRecord(error) && typeof error.message === "string" ? error.message : "Login failed";
      throw new Error(message);
    }
  },

  logout: async () => {
    await clearSession();
  },

  checkError: async (error: unknown) => {
    const err = httpLikeError(error);
    if (err?.status === 401) {
      await clearSession();
      throw error;
    }
  },

  checkAuth: async () => {
    const user = await waitForAuthState();
    if (!user) {
      goToLogin();
      throw new Error("Not authenticated");
    }

    const hasImpersonation = Boolean(localStorage.getItem("impersonatedTenantId"));
    const isSuperAdminFlag = localStorage.getItem("isSuperAdmin") === "1";

    // Fast path: flag and impersonation both present — nothing to do.
    if (isSuperAdminFlag && hasImpersonation) {
      return Promise.resolve();
    }

    // Super admin flag set but no tenant selected — send to tenant selector.
    if (isSuperAdminFlag && !hasImpersonation) {
      throw { redirectTo: "/select-tenant", logoutOnFailure: false };
    }

    // Flag not present in localStorage (cleared storage, migration from sessionStorage, or
    // new device). Check the in-memory cache first (free), then fall back to the API (one
    // call) to detect the super admin role without relying on the persisted flag.
    if (!hasImpersonation) {
      const cachedPermissions = cache.get(user.uid);
      const isSuperAdminFromCache =
        cachedPermissions != null &&
        hasPermission(cachedPermissions, SUPER_ADMIN_IMPERSONATE_PERMISSION);

      if (isSuperAdminFromCache) {
        localStorage.setItem("isSuperAdmin", "1");
        throw { redirectTo: "/select-tenant", logoutOnFailure: false };
      }

      // Cache miss — fetch once to definitively check the role.
      try {
        const permissions = await getCachedPermissions(false);
        if (hasPermission(permissions, SUPER_ADMIN_IMPERSONATE_PERMISSION)) {
          localStorage.setItem("isSuperAdmin", "1");
          throw { redirectTo: "/select-tenant", logoutOnFailure: false };
        }
      } catch (err) {
        // Re-throw our own redirect object; swallow permission fetch errors so a
        // transient API failure does not log the user out on checkAuth.
        if (typeof err === "object" && err !== null && "redirectTo" in err) {
          throw err;
        }
      }
    }

    return Promise.resolve();
  },

  getPermissions: async () => {
    try {
      return await getCachedPermissions(false);
    } catch (error: unknown) {
      const err = httpLikeError(error);
      if (err?.status === 401) {
        throw error;
      }
      console.error("Error getting permissions:", error);
      return {};
    }
  },

  getIdentity: async () => fetchCurrentAdminIdentity(),
};

export default authProvider;
